/**
 * mapRegistry.ts
 *
 * The single source of truth for map assets: projection, geographic bounds, and
 * SVG viewBox per region. `getProfile()` derives a projection profile from here.
 *
 * SVGs are served as static files from `public/assets/deckorate-assets/svg/`
 * and fetched lazily the first time a region is used.
 */

import type { MapProfile, MapRegistryEntry } from "../types";

export const MAP_REGISTRY: Record<string, MapRegistryEntry> = {
  world: {
    name: "World",
    svgPath: "/assets/deckorate-assets/svg/world.svg",
    projection: "robinson",
    bounds: { north: 83.6, south: -56, west: -180, east: 180 },
    viewBox: { width: 1000, height: 507.209 },
    // The Robinson projection normalises to the FULL viewBox frame, but the
    // extracted land shapes don't fill it (ocean margins, no far-south land).
    // Cropping to content would shift markers off (SE drift) — keep the full
    // viewBox so projected points line up with the rendered countries.
    tightCrop: false,
    // This world.svg draws its content offset ~0.033 west of the mathematical
    // Robinson frame. Affine fit of 24 country centroids vs their rendered
    // positions (x RMS 0.027 → 0.004). Without it markers land ~0.03·width
    // (≈0.4″) too far east (London → North Sea, Tokyo → Pacific).
    viewBoxCorrection: { sx: 1.01018, tx: -0.03261, sy: 0.99184, ty: 0.0053 },
  },
  us: {
    name: "United States",
    svgPath: "/assets/deckorate-assets/svg/us.svg",
    projection: "albers",
    bounds: { north: 49.4, south: 24.5, west: -124.8, east: -66.9 },
    viewBox: { width: 959, height: 593 },
    tightCrop: false, // US SVG has Alaska/Hawaii insets — must use full viewBox
    projectionParams: {
      standardParallel1: 29.5,
      standardParallel2: 45.5,
      centralMeridian: -96,
      originLatitude: 37.5,
    },
    // Pre-computed linear fit (raw Albers → viewBox-normalised [0,1]), derived
    // empirically from known US state boundary vertices in this exact SVG.
    projectionMapping: { sx: 1.319646, tx: 0.501417, sy: -2.129238, ty: 0.531736 },
  },
  in: {
    name: "India",
    svgPath: "/assets/deckorate-assets/svg/in.svg",
    projection: "equirectangular",
    bounds: { north: 35.5, south: 6.7, west: 68.1, east: 97.4 },
    viewBox: { width: 1000, height: 983 },
    viewBoxCorrection: { sx: 0.967, tx: 0.0165, sy: 0.9674, ty: 0.0168 },
  },
  cn: {
    name: "China",
    svgPath: "/assets/deckorate-assets/svg/cn.svg",
    projection: "equirectangular",
    bounds: { north: 53.6, south: 15.8, west: 73.6, east: 134.8 },
    viewBox: { width: 1000, height: 624 },
    viewBoxCorrection: { sx: 0.9839, tx: 0.0081, sy: 0.9744, ty: 0.0128 },
  },
  jp: {
    name: "Japan",
    svgPath: "/assets/deckorate-assets/svg/jp.svg",
    projection: "equirectangular",
    bounds: { north: 45.5, south: 24.2, west: 122.9, east: 154 },
    viewBox: { width: 1000, height: 695 },
    viewBoxCorrection: { sx: 0.9688, tx: 0.0156, sy: 0.9564, ty: 0.0226 },
  },
  gb: {
    name: "United Kingdom",
    svgPath: "/assets/deckorate-assets/svg/gb.svg",
    projection: "equirectangular",
    bounds: { north: 60.8, south: 49.9, west: -13.7, east: 1.8 },
    viewBox: { width: 1000, height: 721 },
    viewBoxCorrection: { sx: 0.9392, tx: 0.0304, sy: 0.9212, ty: 0.0422 },
  },
  ca: {
    name: "Canada",
    svgPath: "/assets/deckorate-assets/svg/ca.svg",
    projection: "equirectangular",
    bounds: { north: 83.1, south: 41.7, west: -141, east: -52.6 },
    viewBox: { width: 1000, height: 474 },
    viewBoxCorrection: { sx: 0.9887, tx: 0.0056, sy: 0.9785, ty: 0.0118 },
  },
  au: {
    name: "Australia",
    svgPath: "/assets/deckorate-assets/svg/au.svg",
    projection: "equirectangular",
    bounds: { north: -9.2, south: -43.6, west: 112.9, east: 153.6 },
    viewBox: { width: 1000, height: 849 },
    // Land is drawn inset from the viewBox edges; map projection [0,1] onto the
    // drawn-land rectangle. Fit from city reference points (see scratch diag).
    viewBoxCorrection: { sx: 0.976, tx: 0.012, sy: 0.9717, ty: 0.0141 },
  },
  br: {
    name: "Brazil",
    svgPath: "/assets/deckorate-assets/svg/br.svg",
    projection: "equirectangular",
    bounds: { north: 5.3, south: -33.7, west: -74, east: -28.9 },
    viewBox: { width: 1000, height: 868 },
    viewBoxCorrection: { sx: 0.9784, tx: 0.0108, sy: 0.9738, ty: 0.0124 },
  },
  fr: {
    name: "France",
    svgPath: "/assets/deckorate-assets/svg/fr.svg",
    projection: "equirectangular",
    bounds: { north: 51.1, south: 41.4, west: -5.1, east: 9.6 },
    viewBox: { width: 1000, height: 682 },
    viewBoxCorrection: { sx: 0.9362, tx: 0.0319, sy: 0.9073, ty: 0.0468 },
  },
  de: {
    name: "Germany",
    svgPath: "/assets/deckorate-assets/svg/de.svg",
    projection: "equirectangular",
    bounds: { north: 55.1, south: 47.3, west: 5.9, east: 15 },
    viewBox: { width: 1000, height: 871 },
    viewBoxCorrection: { sx: 0.9016, tx: 0.0492, sy: 0.8801, ty: 0.0565 },
  },
  mx: {
    name: "Mexico",
    svgPath: "/assets/deckorate-assets/svg/mx.svg",
    projection: "equirectangular",
    bounds: { north: 32.7, south: 14.5, west: -118.4, east: -86.7 },
    viewBox: { width: 1000, height: 587 },
    viewBoxCorrection: { sx: 0.9693, tx: 0.0154, sy: 0.9477, ty: 0.0261 },
  },
  it: {
    name: "Italy",
    svgPath: "/assets/deckorate-assets/svg/it.svg",
    projection: "equirectangular",
    bounds: { north: 47.1, south: 35.5, west: 6.6, east: 18.5 },
    viewBox: { width: 1000, height: 977 },
    viewBoxCorrection: { sx: 0.9225, tx: 0.0387, sy: 0.9187, ty: 0.0396 },
  },
  kr: {
    name: "South Korea",
    svgPath: "/assets/deckorate-assets/svg/kr.svg",
    projection: "equirectangular",
    bounds: { north: 38.6, south: 33.2, west: 124.6, east: 130.9 },
    viewBox: { width: 1000, height: 877 },
    viewBoxCorrection: { sx: 0.8608, tx: 0.0684, sy: 0.8472, ty: 0.0781 },
  },
  id: {
    name: "Indonesia",
    svgPath: "/assets/deckorate-assets/svg/id.svg",
    projection: "equirectangular",
    bounds: { north: 5.9, south: -10.9, west: 95, east: 141 },
    viewBox: { width: 1000, height: 379 },
    viewBoxCorrection: { sx: 0.9786, tx: 0.0107, sy: 0.9459, ty: 0.0285 },
  },
  za: {
    name: "South Africa",
    svgPath: "/assets/deckorate-assets/svg/za.svg",
    projection: "equirectangular",
    bounds: { north: -22.1, south: -34.2, west: 16.5, east: 32.9 },
    viewBox: { width: 1000, height: 753 },
    viewBoxCorrection: { sx: 0.9426, tx: 0.0287, sy: 0.9207, ty: 0.0385 },
  },
  ng: {
    name: "Nigeria",
    svgPath: "/assets/deckorate-assets/svg/ng.svg",
    projection: "equirectangular",
    bounds: { north: 13.9, south: 4.3, west: 2.7, east: 14.7 },
    viewBox: { width: 1000, height: 815 },
    viewBoxCorrection: { sx: 0.9229, tx: 0.0385, sy: 0.9063, ty: 0.0472 },
  },
};

const svgCache = new Map<string, string>();

/** Fetch a map SVG (cached). */
export async function fetchMapSvg(regionId: string): Promise<string> {
  const cached = svgCache.get(regionId);
  if (cached) return cached;

  const registry = MAP_REGISTRY[regionId];
  if (!registry) throw new Error(`Unknown map region: ${regionId}`);

  const res = await fetch(registry.svgPath);
  if (!res.ok) {
    throw new Error(`Failed to load map SVG: ${res.status} ${res.statusText}`);
  }
  const svg = await res.text();
  svgCache.set(regionId, svg);
  return svg;
}

/** Auto-detect the smallest registry map whose bounds contain all points. */
export function detectBestMap(
  locations: ReadonlyArray<{ lat: number; lon: number }>,
): string {
  if (!locations || locations.length === 0) return "world";

  const lats = locations.map((l) => l.lat);
  const lons = locations.map((l) => l.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  let bestId = "world";
  let bestArea = Infinity;
  for (const [id, reg] of Object.entries(MAP_REGISTRY)) {
    if (id === "world") continue;
    const b = reg.bounds;
    if (minLat >= b.south && maxLat <= b.north && minLon >= b.west && maxLon <= b.east) {
      const area = (b.east - b.west) * (b.north - b.south);
      if (area < bestArea) {
        bestArea = area;
        bestId = id;
      }
    }
  }
  return bestId;
}

/** `[{id, name}]` for the region dropdown. */
export function getMapList(): { id: string; name: string }[] {
  return Object.entries(MAP_REGISTRY).map(([id, reg]) => ({ id, name: reg.name }));
}

/** Build the projection profile the engine needs from a registry entry. */
export function getProfile(regionId: string): MapProfile | null {
  const reg = MAP_REGISTRY[regionId];
  if (!reg) return null;
  return {
    id: regionId,
    projection: reg.projection,
    bounds: reg.bounds,
    projectionParams: reg.projectionParams ?? null,
    projectionMapping: reg.projectionMapping ?? null,
    viewBoxCorrection: reg.viewBoxCorrection ?? null,
  };
}
